import { Suspense, lazy, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { HelmetProvider } from "react-helmet-async";
import { ProtectedRoute, PublicRoute } from "./routes/ProtectedRoute";
import Layout from "./components/layout/Layout";
import { useDispatch } from "react-redux";
import { validateToken } from "./features/authSlice";
import DynamicIndexRedirect from "./components/layout/DynamicIndexRedirect";
import { ToastContainer } from "react-toastify";

// Public pages
const LoginPage = lazy(() => import("./pages/Login"));
// const RegisterPage = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const VerifyOtp = lazy(() => import("./pages/VerifyOtp"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const CustomerSelfRegister = lazy(() => import("./pages/CustomerSelfRegister"));
const CustomerFeedback = lazy(() => import("./pages/CustomerFeedback"));
const TrackStatus = lazy(() => import("./pages/TrackStatus"));

// Protected pages for Layout
const CustomerRegister = lazy(() => import("./pages/CustomerRegister"));
const CustomerManagement = lazy(() => import("./pages/CustomerManagement"));
const CustomerFeedbackList = lazy(() => import("./pages/CustomerFeedbackList"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ServiceManagement = lazy(() => import("./pages/ServiceManagement"));
const CompanyList = lazy(() => import("./pages/CompanyList"));
const BranchList = lazy(() => import("./pages/BranchList"));
const BranchSettingList = lazy(() => import("./pages/BranchSettingList"));
const CompanyUserList = lazy(() => import("./pages/CompanyUserList"));
const PlatformUserList = lazy(() => import("./pages/PlatformUserList"));
const RoleList = lazy(() => import("./pages/RoleList"));
const ModuleList = lazy(() => import("./pages/ModuleList"));
const RolePermissions = lazy(() => import("./pages/RolePermissions"));
const SubscriptionPlanList = lazy(() => import("./pages/SubscriptionPlanList"));
const EmployeeDashboard = lazy(() => import("./pages/EmployeeDashboard"));
const UserActionLogList = lazy(() => import("./pages/UserActionLogList"));
const StaffPerformanceReport = lazy(
  () => import("./pages/StaffPerformanceReport"),
);
const QueueTokens = lazy(() => import("./pages/QueueTokens"));
const MyProfile = lazy(() => import("./pages/MyProfile"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const CustomerDetail = lazy(() => import("./pages/CustomerDetail"));

const theme = createTheme({
  palette: {
    primary: { main: "#1976d2" },
    secondary: { main: "#dc004e" },
  },
  typography: {
    fontFamily: "\"Inter\", system-ui, -apple-system, sans-serif",
  },
});

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    if (token) {
      dispatch(validateToken());
    }
  }, [dispatch]);

  return (
    <ThemeProvider theme={theme}>
      <HelmetProvider>
        <CssBaseline />
        <Router>
          <ToastContainer
            position="top-right"
            autoClose={4000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
          />
          <Suspense>
            <Routes>
              <Route
                path="/login"
                element={<PublicRoute element={<LoginPage />} />}
              />
              <Route
                path="/forgot-password"
                element={<PublicRoute element={<ForgotPassword />} />}
              />
              <Route
                path="/verify-otp"
                element={<PublicRoute element={<VerifyOtp />} />}
              />
              <Route
                path="/reset-password"
                element={<PublicRoute element={<ResetPassword />} />}
              />
              <Route
                path="/customer-self-register"
                element={<PublicRoute element={<CustomerSelfRegister />} />}
              />
              <Route
                path="/customer-feedback"
                element={<PublicRoute element={<CustomerFeedback />} />}
              />
              <Route
                path="/track"
                element={<PublicRoute element={<TrackStatus />} />}
              />
              {/* Protected Routes with Layout */}
              <Route path="/" element={<ProtectedRoute element={<Layout />} />}>
                <Route index element={<DynamicIndexRedirect />} />
                <Route path="AdminDashboard" element={<AdminDashboard />} />
                <Route
                  path="EmployeeDashboard"
                  element={<EmployeeDashboard />}
                />
                <Route path="companies" element={<CompanyList />} />
                <Route path="branches" element={<BranchList />} />
                <Route path="branch-settings" element={<BranchSettingList />} />
                <Route path="platform-users" element={<PlatformUserList />} />
                <Route path="company-users" element={<CompanyUserList />} />
                <Route path="modules" element={<ModuleList />} />
                <Route path="roles" element={<RoleList />} />
                <Route path="role-permissions" element={<RolePermissions />} />
                <Route path="services" element={<ServiceManagement />} />
                <Route
                  path="subscription-plans"
                  element={<SubscriptionPlanList />}
                />
                <Route path="customers" element={<CustomerManagement />} />
                <Route
                  path="customer-register"
                  element={<CustomerRegister />}
                />
                <Route
                  path="customer-feedbacks"
                  element={<CustomerFeedbackList />}
                />
                <Route path="customer/:id" element={<CustomerDetail />} />
                <Route path="profile" element={<MyProfile />} />
                <Route
                  path="user-action-logs"
                  element={<UserActionLogList />}
                />
                <Route
                  path="staff-performance"
                  element={<StaffPerformanceReport />}
                />
                <Route path="queue-tokens" element={<QueueTokens />} />
                <Route path="change-password" element={<ChangePassword />} />
              </Route>
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Suspense>
        </Router>
      </HelmetProvider>
    </ThemeProvider>
  );
}

export default App;
